import React from 'react';
import { cn } from '../../lib/utils';

export interface TabItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  badge?: string | number;
}

interface TabsProps {
  tabs: TabItem[];
  activeTab: string;
  onChange: (tab: string) => void;
  variant?: 'default' | 'pills' | 'underline';
  className?: string;
}

const containerClasses: Record<NonNullable<TabsProps['variant']>, string> = {
  default: 'border-b border-neutral-200 dark:border-neutral-700',
  pills: 'flex gap-1',
  underline: 'border-b border-neutral-200 dark:border-neutral-700',
};

const tabClasses: Record<NonNullable<TabsProps['variant']>, string> = {
  default: 'border-b-2 border-transparent -mb-px px-4 py-2.5',
  pills: 'rounded-md px-3 py-2',
  underline: 'border-b-2 border-transparent -mb-px px-4 py-2.5',
};

const activeClasses: Record<NonNullable<TabsProps['variant']>, string> = {
  default: 'border-primary-600 text-primary-700 dark:border-primary-400 dark:text-primary-300',
  pills: 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200',
  underline: 'border-primary-600 text-primary-700 dark:border-primary-400 dark:text-primary-300',
};

const inactiveClasses: Record<NonNullable<TabsProps['variant']>, string> = {
  default: 'text-neutral-500 hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-neutral-100',
  pills: 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-neutral-100',
  underline: 'text-neutral-500 hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-neutral-100',
};

export function Tabs({ tabs, activeTab, onChange, variant = 'default', className }: TabsProps) {
  const focusableTabs = tabs.filter(tab => !tab.disabled);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = focusableTabs.findIndex(tab => tab.id === activeTab);
    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1 + focusableTabs.length) % focusableTabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + focusableTabs.length) % focusableTabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = focusableTabs.length - 1;

    if (nextIndex === currentIndex || nextIndex < 0) return;

    event.preventDefault();
    const nextTab = focusableTabs[nextIndex];
    onChange(nextTab.id);
    window.requestAnimationFrame(() => document.getElementById(`${nextTab.id}-trigger`)?.focus());
  };

  return (
    <div
      className={cn('flex min-w-0', containerClasses[variant], className)}
      role="tablist"
      aria-orientation="horizontal"
      aria-label="Abschnitte"
      onKeyDown={handleKeyDown}
    >
      {tabs.map(tab => {
        const active = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            id={`${tab.id}-trigger`}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`${tab.id}-panel`}
            aria-disabled={tab.disabled || undefined}
            tabIndex={active ? 0 : -1}
            onClick={() => !tab.disabled && onChange(tab.id)}
            disabled={tab.disabled}
            className={cn(
              'inline-flex min-w-0 flex-shrink-0 items-center gap-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900',
              tabClasses[variant],
              active ? activeClasses[variant] : inactiveClasses[variant],
              tab.disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            {tab.icon && <span className="flex-shrink-0" aria-hidden="true">{tab.icon}</span>}
            <span className="truncate">{tab.label}</span>
            {tab.badge !== undefined && tab.badge !== null && (
              <span
                className={cn(
                  'inline-flex min-w-[1.25rem] items-center justify-center rounded-md px-1.5 py-0.5 text-xs font-semibold',
                  active
                    ? 'bg-primary-200 text-primary-900 dark:bg-primary-800 dark:text-primary-100'
                    : 'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200'
                )}
                aria-label={typeof tab.badge === 'number' ? `${tab.badge} Einträge` : undefined}
              >
                {tab.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

interface TabPanelProps {
  id: string;
  children: React.ReactNode;
  active: boolean;
  className?: string;
}

export function TabPanel({ id, children, active, className }: TabPanelProps) {
  if (!active) return null;
  return (
    <div
      role="tabpanel"
      id={`${id}-panel`}
      aria-labelledby={`${id}-trigger`}
      className={cn('rounded-b-md outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900', className)}
    >
      {children}
    </div>
  );
}
